<template>
  <li
    v-if="!childrenLinks && isHeader"
    :class="{ headerLink: true, className, collapsed }"
  >
    <router-link
      v-if="!link.includes('http')"
      :to="link"
      class="sidebar-link d-flex"
      active-class="router-link-active"
      @click.native="closeAllMenus"
    >
      <span class="icon">
        <i v-if="iconName" :class="fullIconName"></i>
        <img v-if="imgUrl" :src="imgUrl" />
      </span>
      <span v-if="showHeader && !collapsed" class="header-text">{{
        header
      }}</span>
      <sup
        v-if="label && !collapsed"
        :class="'text-' + labelColor"
        class="headerLabel"
        >{{ label }}</sup
      >
      <b-badge v-if="badge && !collapsed" variant="primary" pill>{{
        badge
      }}</b-badge>
    </router-link>

    <a
      v-if="link.includes('http')"
      :href="link"
      class="sidebar-link d-flex"
      target="_blank"
      @click="closeAllMenus"
    >
      <span class="icon">
        <i v-if="iconName" :class="fullIconName"></i>
        <img v-if="imgUrl" :src="imgUrl" />
      </span>
      <span v-if="showHeader && !collapsed" class="header-text">{{
        header
      }}</span>
      <sup
        v-if="label && !collapsed"
        :class="'text-' + labelColor"
        class="headerLabel"
        >{{ label }}</sup
      >
      <b-badge v-if="badge && !collapsed" variant="primary" pill>{{
        badge
      }}</b-badge>
    </a>

    <div v-if="collapsed" class="nav-tooltip">{{ header }}</div>
  </li>

  <li
    v-else-if="childrenLinks"
    :class="{ headerLink: true, className, collapsed }"
  >
    <div @click="togglePanelCollapse(link)" class="dropdown-toggle">
      <router-link
        :to="link"
        custom
        v-slot="{ navigate, isActive: routerActive, isExactActive }"
      >
        <a
          @click="navigate"
          class="d-flex sidebar-link"
          :class="{
            'router-link-active': routerActive,
            'router-link-exact-active': isExactActive,
          }"
        >
          <span class="icon">
            <i v-if="iconName" :class="fullIconName"></i>
            <img v-if="imgUrl" :src="imgUrl" />
          </span>
          <span v-if="showHeader && !collapsed" class="header-text">{{
            header
          }}</span>
          <sup
            v-if="label && !collapsed"
            :class="'text-' + labelColor"
            class="ml-1 headerLabel"
            >{{ label }}</sup
          >
          <div
            v-if="!collapsed"
            :class="{ caretWrapper: true, carretActive: isActive }"
          >
            <i class="fa fa-chevron-down" />
          </div>
        </a>
      </router-link>
      <div v-if="collapsed" class="nav-tooltip">{{ header }}</div>
    </div>

    <transition name="menu-collapse">
      <div v-if="!collapsed && isActive" class="custom-collapse">
        <ul class="sub-menu">
          <li v-for="child in childrenLinks" :key="child.link" class="child-link">
            <router-link
              v-if="!child.link.includes('http')"
              :to="child.link"
              active-class="router-link-active"
              exact-active-class="router-link-exact-active"
              class="child-link-item"
              @click.native="closeAllMenus"
            >
              <span>{{ child.header }}</span>
              <sup
                v-if="child.label"
                :class="'text-' + (child.labelColor || 'warning')"
                class="headerLabel"
                >{{ child.label }}</sup
              >
            </router-link>

            <a v-else :href="child.link" target="_blank" class="child-link-item" @click="closeAllMenus">
              <span>{{ child.header }}</span>
              <sup
                v-if="child.label"
                :class="'text-' + (child.labelColor || 'warning')"
                class="headerLabel"
                >{{ child.label }}</sup
              >
            </a>
          </li>
        </ul>
      </div>
    </transition>
  </li>

  <!-- Regular non-header link -->
  <li v-else :class="{ collapsed }">
    <router-link
      :to="index !== 'menu' && link"
      active-class="router-link-active"
      exact-active-class="router-link-exact-active"
      @click.native="closeAllMenus"
    >
      <span v-if="!collapsed">{{ header }}</span>
      <sup
        v-if="label && !collapsed"
        :class="'text-' + labelColor"
        class="headerLabel"
        >{{ label }}</sup
      >
    </router-link>
    <div v-if="collapsed" class="nav-tooltip">{{ header }}</div>
  </li>
</template>

<script>
  import { mapState, mapActions } from "vuex";

  export default {
    name: "NavLink",
    props: {
      badge: { type: String, default: "" },
      header: { type: String, default: "" },
      iconName: { type: String, default: "" },
      c: { type: String, default: "" },
      headerLink: { type: String, default: "" },
      link: { type: String, default: "" },
      childrenLinks: { type: Array, default: null },
      className: { type: String, default: "" },
      isHeader: { type: Boolean, default: false },
      deep: { type: Number, default: 0 },
      activeItem: { type: String, default: "" },
      label: { type: String },
      labelColor: { type: String, default: "warning" },
      index: { type: String },
      imgUrl: { type: String },
      collapsed: { type: Boolean, default: false },
    },
    methods: {
      ...mapActions("layout", ["changeSidebarActive"]),
      togglePanelCollapse(link) {
        // If this item is already active, toggle it closed
        if (this.isActive) {
          this.changeSidebarActive('');
        } else {
          // Just set this link as active, immediately
          this.changeSidebarActive(link);
        }
      },
      closeAllMenus() {
        // Close all menus when any regular link is clicked
        // Only do this if it's not a parent with children
        if (!this.childrenLinks) {
          this.changeSidebarActive('');
        }
      }
    },
    // Watch for route changes to update menu state
    watch: {
      $route: {
        handler() {
          // When route changes, close all menus
          this.changeSidebarActive('');
        },
        immediate: true
      }
    },
    computed: {
      fullIconName() {
        return `fi ${this.iconName}`;
      },
      isActive() {
        return this.activeItem === this.link;
      },
      ...mapState("layout", {
        showHeader: (state) => !state.sidebarClose || state.sidebarStatic,
      }),
    },
  };
</script>

<style lang="scss">
  @import "../../../styles/app";

$highlight-color: $error-red;

  .sidebar-link {
    height: 59px;
  }

  .headerLink {
    width: 100%;
    overflow-x: hidden;

    @media (min-width: map_get($grid-breakpoints, lg)) and (min-height: $screen-lg-height),
      (max-width: map_get($grid-breakpoints, md)) {
      font-size: 13px;
    }

    a {
      display: block;
      color: var(--redstone-dark-blue-color);
      text-decoration: none;
      cursor: pointer;
    }

    &:last-child > a {
      border-bottom: 1px solid $sidebar-item-border-color;
    }

    > a,
    > div a {
      align-items: center;
      position: relative;
      padding-left: 64px;
      line-height: 58px;
      border-top: 1px solid $sidebar-item-border-color;
      font-size: 14px;
      font-weight: $font-weight-normal;

      &:hover {
        background-color: var(--sidebar-item-hover-bg-color);
        color:  var(--redstone-dark-blue-color);
        text-decoration: none;
      }

      > i {
        margin-right: 7px;
      }
    }

    .icon {
      font-size: $font-size-larger;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 12px;
      left: 16px;
      width: 32px;
      height: 32px;
      line-height: 28px;
      text-align: center;

      img {
        max-width: 28px;
        max-height: 28px;
      }
    }

    .badge {
      float: right;
      line-height: 8px;
      margin-top: 6px;
      margin-right: 15px;
      padding: 7px;
      background: var(--sidebar-badge-bg);

      @media (min-width: map_get($grid-breakpoints, lg)) and (min-height: $screen-lg-height),
        (max-width: map_get($grid-breakpoints, md)) {
        margin-top: 16px;
      }
    }

    .collapse > ul > li a {
      padding-left: 50px;
    }
  }

  // For collapsed state
  .headerLink.collapsed {
    > a,
    > div a {
      padding-left: 0;
      display: flex;
      justify-content: center;
      padding: 0 5px;

      .header-text,
      .badge,
      .caretWrapper {
        display: none;
      }

      .icon {
        position: relative;
        top: auto;
        left: auto;
        margin: 12px auto;
      }
    }

    &:hover .nav-tooltip {
      opacity: 1;
      visibility: visible;
    }
  }

  .nav-tooltip {
    position: absolute;
    left: 65px;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  li.collapsed:hover .nav-tooltip {
    opacity: 1;
    visibility: visible;
  }

  .dropdown-toggle {
    position: relative;
    cursor: pointer;
  }

  .headerLabel {
    font-weight: 600;
  }

  .caretWrapper {
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: 22px;
    color: var(--redstone-dark-blue-color);

    i {
      transition: transform 0.2s ease;
      font-size: 12px;
    }
  }

  .carretActive i {
    transform: rotate(180deg);
  }

  a.router-link-active,
  a.router-link-exact-active {
    border-left: 3px solid $highlight-color;
    color: $highlight-color;
    font-size: $font-size-larger;
    font-weight: $font-weight-semi-bold;

    .icon {
      margin-left: -3px;

      i {
        filter: brightness(0) saturate(100%) invert(29%) sepia(96%)
          saturate(3500%) hue-rotate(353deg) brightness(70%) contrast(108%);
        color: $highlight-color !important;
      }
    }

    .header-text {
      color: $highlight-color;
    }
  }

  .menu-collapse-enter-active,
  .menu-collapse-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
  }
  .menu-collapse-enter,
  .menu-collapse-leave-to {
    opacity: 0;
    max-height: 0;
  }
  .menu-collapse-enter-to,
  .menu-collapse-leave {
    opacity: 1;
    max-height: 300px;
  }

  .custom-collapse {
    overflow: hidden;
  }

  i[class^="fi"],
  .header-text {
    transition:
      color 0.3s ease,
      filter 0.3s ease;
  }

  .dropdown-toggle::after{ 
    display: none !important;
  }
</style>